import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import OrdersTab from './OrdersTab';
import RoutePlanTab from './RoutePlanTab';
import WarehouseTab from './WarehouseTab';
import ForecastTab from './ForecastTab';
import { AdminPageShell, TabBar } from '../../../components/admin/Shared';
import { fetchAdminPartners } from '../../../services/adminPartners';

type Tab = 'orders' | 'route-plan' | 'warehouse' | 'forecast';

const TABS: { id: Tab; label: string; path: string }[] = [
  { id: 'orders', label: 'Orders', path: '/admin/logistics' },
  { id: 'route-plan', label: 'Route Plan', path: '/admin/logistics/route-plan' },
  { id: 'warehouse', label: 'Warehouse', path: '/admin/logistics/warehouse' },
  { id: 'forecast', label: 'Forecast', path: '/admin/logistics/forecast' },
];

// Legacy paths resolve to the same tabs so existing bookmarks keep working.
const LEGACY_PATHS: Record<string, Tab> = {
  '/admin/route-plan': 'route-plan',
  '/admin/warehouse': 'warehouse',
  '/admin/warehouse-forecast': 'forecast',
};

function resolveTab(pathname: string): Tab {
  if (LEGACY_PATHS[pathname]) return LEGACY_PATHS[pathname];
  const match = TABS.find((t) => pathname === t.path);
  return match ? match.id : 'orders';
}

/** `?partnerId=unassigned` — the orders no partner has yet. Orders tab only. */
export const UNASSIGNED = 'unassigned';
/** `?partnerId=all` — every order, whoever has it. Orders tab only. */
export const ALL_ORDERS = 'all';

type Scope = number | typeof UNASSIGNED | typeof ALL_ORDERS | null;

export function readScopeFromSearch(search: string): Scope {
  const raw = new URLSearchParams(search).get('partnerId');
  if (!raw) return null;
  if (raw === UNASSIGNED) return UNASSIGNED;
  if (raw === ALL_ORDERS) return ALL_ORDERS;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function buildSearch(scope: Scope): string {
  if (scope == null) return '';
  return `?partnerId=${scope}`;
}

function LogisticsPage({ history, location }: RouteComponentProps) {
  const active: Tab = resolveTab(location.pathname);
  const scope = readScopeFromSearch(location.search);
  const partnerId = typeof scope === 'number' ? scope : null;

  const [partnerOptions, setPartnerOptions] = useState<{ id: number; name: string }[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(true);

  // The partners table itself. This used to be derived from partner *users*, so
  // a partner with nobody under it yet — every partner, the day it is added —
  // never appeared here.
  useEffect(() => {
    let cancelled = false;
    fetchAdminPartners()
      .then((partners) => {
        if (cancelled) return;
        setPartnerOptions(partners.map((p) => ({ id: p.id, name: p.name })));
      })
      .catch(() => {
        // Silent — picker just stays empty, tabs show "select a partner" state.
      })
      .finally(() => {
        if (!cancelled) setPartnersLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setScope = (next: Scope) => {
    history.push({ pathname: location.pathname, search: buildSearch(next) });
  };

  const goTab = (path: string) => {
    history.push({ pathname: path, search: buildSearch(scope) });
  };

  const renderTab = () => {
    switch (active) {
      case 'orders':
        return (
          <OrdersTab
            partnerId={partnerId}
            unassigned={scope === UNASSIGNED}
            allOrders={scope === ALL_ORDERS}
          />
        );
      case 'route-plan':
        return <RoutePlanTab partnerId={partnerId} />;
      case 'warehouse':
        return <WarehouseTab partnerId={partnerId} />;
      case 'forecast':
        return <ForecastTab partnerId={partnerId} />;
    }
  };

  const partnerSelectLabel = useMemo(() => {
    if (partnersLoading) return 'Loading…';
    if (partnerOptions.length === 0) return 'No partners available';
    return undefined;
  }, [partnersLoading, partnerOptions.length]);

  return (
    <AdminPageShell
      title="Logistics"
      onBack={() => history.push('/admin/dashboard')}
      contextBar={
        <div className="admin-partner-picker">
          <label
            className="admin-partner-picker__label"
            htmlFor="logistics-partner-picker"
          >
            Partner
          </label>
          <select
            id="logistics-partner-picker"
            className="admin-partner-picker__select admin-select"
            value={scope ?? ''}
            onChange={(e) => setScope(readScopeFromSearch(`?partnerId=${e.target.value}`))}
            disabled={partnersLoading}
          >
            <option value="">
              {partnerSelectLabel ?? 'Select a partner…'}
            </option>
            <option value={ALL_ORDERS}>All orders</option>
            <option value={UNASSIGNED}>Unassigned orders</option>
            {(partnerOptions.length > 0 || partnerId != null) && (
              <optgroup label="Partners">
                {partnerOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
                {partnerId != null && !partnerOptions.some((p) => p.id === partnerId) && (
                  // A link or bookmark to a partner that has since been switched
                  // off. Without this the picker would read "Select a partner…"
                  // over a page showing that partner's orders.
                  <option value={partnerId}>Partner #{partnerId}</option>
                )}
              </optgroup>
            )}
          </select>
        </div>
      }
      tabs={
        <TabBar<Tab>
          items={TABS.map((t) => ({ id: t.id, label: t.label }))}
          activeId={active}
          onChange={(id) => {
            const next = TABS.find((t) => t.id === id);
            if (next) goTab(next.path);
          }}
          ariaLabel="Logistics sections"
        />
      }
    >
      {renderTab()}
    </AdminPageShell>
  );
}

export default withRouter(LogisticsPage);
